import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconChevronDown,
  IconChevronUp,
  IconLayoutSidebar,
  IconSearch,
  IconShieldLock,
  IconX,
  IconZoomIn,
  IconZoomOut,
} from '@tabler/icons-react';
import { Badge, Button, IconButton, SpinnerComponent, TextInput } from 'ui-kit';

import type { OpenedDocument } from './core';
import { PageView } from './PageView';
import { MIN_TERM_LENGTH, findMatches, normalizeTerm, type SearchMatch } from './pdfSearch';
import { StampOverlay, currentStampCount, formatOpenedAt } from './StampOverlay';
import { Thumbnails } from './Thumbnails';
import { usePdfDocument } from './usePdfDocument';

import './irmViewer.css';

const PAGE_GAP_PX = 16;
const PAGE_PADDING_PX = 48;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
const SEARCH_DEBOUNCE_MS = 350;
const NOTICE_MS = 3500;

type ZoomMode = 'width' | 'page' | number;

type ToolButtonProps = Parameters<typeof IconButton>[0];

const ToolButton: FC<ToolButtonProps> = (props) => (
  <IconButton {...props} aria-disabled={props.disabled ? true : undefined} />
);

const capitalize = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

export const DocumentViewer: FC<{
  document: OpenedDocument;
  saleKey: string;
  suspended?: boolean;
  onClose: () => void;
}> = ({ document: opened, saleKey, suspended = false, onClose }) => {
  const loaded = usePdfDocument(opened.bytes);
  const frameRef = useRef<HTMLDivElement>(null);
  const searchBox = useRef<HTMLDivElement>(null);
  const pageRefs = useRef(new Map<number, HTMLElement>());
  const ratios = useRef(new Map<number, number>());
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const [panel, setPanel] = useState({ width: 0, height: 0 });
  const [zoom, setZoomState] = useState<ZoomMode>('width');
  const anchorPage = useRef<number>();
  const pageBeforeSuspend = useRef<number>();
  const suspendedRef = useRef(suspended);
  const searchInterrupted = useRef(false);
  const [searchRun, setSearchRun] = useState(0);
  const currentPageRef = useRef(1);
  const editingPage = useRef(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [sidebar, setSidebar] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<SearchMatch[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [searching, setSearching] = useState(false);
  const [activeMatch, setActiveMatch] = useState(0);
  const [matchScrollRequest, setMatchScrollRequest] = useState(0);
  const [notice, setNotice] = useState<string>();

  const stampCount = useMemo(currentStampCount, []);
  const stamp = [opened.viewerName, formatOpenedAt(opened.openedAt), opened.sourceIp]
    .filter(Boolean)
    .join(' · ');
  const title = opened.docName || 'Protected document';
  const pdfReady = loaded.status === 'ready' ? loaded : undefined;
  const pageCount = pdfReady?.pdf.numPages ?? 0;

  useEffect(() => {
    if (!scroller || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setPanel({ width, height });
      }
    });
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [scroller]);

  const widthScale =
    pdfReady && panel.width > 0
      ? Math.min(
          MAX_ZOOM,
          Math.max(MIN_ZOOM, (panel.width - PAGE_PADDING_PX) / pdfReady.firstPage.width),
        )
      : 1;
  const pageScale =
    pdfReady && panel.height > 0
      ? Math.min(
          widthScale,
          Math.max(MIN_ZOOM, (panel.height - PAGE_GAP_PX * 2) / pdfReady.firstPage.height),
        )
      : widthScale;
  let scale = zoom;
  if (zoom === 'width') {
    scale = widthScale;
  } else if (zoom === 'page') {
    scale = pageScale;
  }
  const effectiveScale = scale as number;
  const placeholder = pdfReady
    ? {
        width: pdfReady.firstPage.width * effectiveScale,
        height: pdfReady.firstPage.height * effectiveScale,
      }
    : { width: 0, height: 0 };

  const flash = useCallback((message: string) => {
    setNotice(message);
  }, []);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }
    const timer = setTimeout(() => setNotice(undefined), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const register = useCallback((pageNumber: number, element: HTMLElement | null) => {
    if (element) {
      pageRefs.current.set(pageNumber, element);
    } else {
      pageRefs.current.delete(pageNumber);
      ratios.current.delete(pageNumber);
    }
  }, []);

  const onVisibility = useCallback((pageNumber: number, ratio: number) => {
    ratios.current.set(pageNumber, ratio);
    let best = 0;
    let bestRatio = 0;
    ratios.current.forEach((value, page) => {
      if (value > bestRatio || (value === bestRatio && value > 0 && page < best)) {
        best = page;
        bestRatio = value;
      }
    });
    if (best > 0) {
      setCurrentPage(best);
      if (!editingPage.current) {
        setPageInput(String(best));
      }
    }
  }, []);

  const goToPage = useCallback(
    (pageNumber: number) => {
      const target = Math.min(Math.max(1, pageNumber), Math.max(1, pageCount));
      setCurrentPage(target);
      setPageInput(String(target));
      const element = pageRefs.current.get(target);
      if (element && typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({ block: 'start' });
      }
    },
    [pageCount],
  );

  const goToPageRef = useRef(goToPage);
  goToPageRef.current = goToPage;

  const changeZoom = useCallback(
    (mode: ZoomMode) => {
      if (mode === zoom) {
        return;
      }
      anchorPage.current = currentPage;
      setZoomState(mode);
    },
    [currentPage, zoom],
  );

  const zoomBy = useCallback(
    (delta: number) => {
      const base = typeof zoom === 'number' ? zoom : zoom === 'page' ? pageScale : widthScale;
      changeZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((base + delta) * 100) / 100)));
    },
    [zoom, pageScale, widthScale, changeZoom],
  );

  currentPageRef.current = currentPage;
  suspendedRef.current = suspended;

  useEffect(() => {
    if (suspended) {
      pageBeforeSuspend.current = currentPageRef.current;
      return;
    }
    const page = pageBeforeSuspend.current;
    pageBeforeSuspend.current = undefined;
    const element = page === undefined ? undefined : pageRefs.current.get(page);
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'start' });
    }
    if (searchInterrupted.current) {
      searchInterrupted.current = false;
      setSearchRun((run) => run + 1);
    }
  }, [suspended]);

  useEffect(() => {
    const page = anchorPage.current;
    anchorPage.current = undefined;
    const element = page === undefined ? undefined : pageRefs.current.get(page);
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'start' });
    }
  }, [zoom, effectiveScale]);

  useEffect(() => {
    const term = normalizeTerm(query);
    if (!pdfReady || term.length < MIN_TERM_LENGTH) {
      setMatches([]);
      setTruncated(false);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      findMatches(pdfReady.pdf, term, () => cancelled || suspendedRef.current)
        .then((result) => {
          if (cancelled) {
            return;
          }
          if (result.cancelled) {
            if (suspendedRef.current) {
              searchInterrupted.current = true;
            } else {
              setSearchRun((run) => run + 1);
            }
            return;
          }
          setMatches(result.matches);
          setTruncated(result.truncated);
          setActiveMatch(0);
          setSearching(false);
          if (result.matches.length > 0) {
            setMatchScrollRequest((request) => request + 1);
            goToPageRef.current(result.matches[0].page);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setSearching(false);
          }
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, pdfReady, searchRun]);

  const stepMatch = (direction: 1 | -1) => {
    if (matches.length === 0) {
      return;
    }
    const next = (activeMatch + direction + matches.length) % matches.length;
    setActiveMatch(next);
    setMatchScrollRequest((request) => request + 1);
    goToPage(matches[next].page);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const modified = event.ctrlKey || event.metaKey;
      if (modified && (key === 's' || key === 'p')) {
        event.preventDefault();
        flash('Saving and printing are turned off for protected documents.');
        return;
      }
      if (suspendedRef.current) {
        return;
      }
      if (modified && key === 'f') {
        event.preventDefault();
        const input = searchBox.current?.querySelector('input');
        input?.focus();
        input?.select();
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) {
        if (event.key === 'Escape' && searchBox.current?.contains(target)) {
          setQuery('');
          (target as HTMLInputElement).blur();
        }
        return;
      }
      if (modified || event.altKey) {
        return;
      }
      if (event.key === '+' || event.key === '=') {
        zoomBy(ZOOM_STEP);
      } else if (event.key === '-') {
        zoomBy(-ZOOM_STEP);
      } else if (event.key === '0') {
        changeZoom('width');
      } else if (event.key === 'Home') {
        goToPage(1);
      } else if (event.key === 'End') {
        goToPage(pageCount);
      }
    };
    const onContextMenu = (event: Event) => event.preventDefault();
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('contextmenu', onContextMenu);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('contextmenu', onContextMenu);
    };
  }, [changeZoom, flash, goToPage, pageCount, zoomBy]);

  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(window.document.fullscreenElement));
    window.document.addEventListener('fullscreenchange', onChange);
    return () => {
      window.document.removeEventListener('fullscreenchange', onChange);
      if (window.document.fullscreenElement) {
        void window.document.exitFullscreen?.();
      }
    };
  }, []);

  const toggleFullscreen = () => {
    if (window.document.fullscreenElement) {
      void window.document.exitFullscreen?.();
    } else {
      void frameRef.current?.requestFullscreen?.();
    }
  };

  const activePage = matches[activeMatch]?.page;
  const activeOrdinal = matches[activeMatch]?.ordinal;
  const term = normalizeTerm(query);
  const searchSummary = (() => {
    if (suspended || term.length < MIN_TERM_LENGTH) {
      return '';
    }
    if (searching) {
      return 'Searching…';
    }
    if (matches.length === 0) {
      return 'No matches';
    }
    return `${activeMatch + 1} of ${matches.length}${truncated ? '+' : ''}`;
  })();

  const zoomLabel = zoom === 'width' ? 'Fit width' : zoom === 'page' ? 'Fit page' : '';
  const zoomPercent = `${Math.round(effectiveScale * 100)}%`;
  const fullscreenSupported = Boolean(window.document.fullscreenEnabled);

  const inertWhenSuspended = suspended ? ({ inert: '' } as Record<string, string>) : {};

  return (
    <div
      ref={frameRef}
      data-testid="irm-viewer-frame"
      className="flex min-h-0 flex-1 flex-col bg-background-canvas light:bg-gray-100"
    >
      <div
        hidden={suspended}
        {...inertWhenSuspended}
        style={suspended ? { display: 'none' } : undefined}
        data-testid="irm-viewer-root"
        className="relative flex min-h-0 flex-1 flex-col bg-background-canvas text-white light:bg-gray-100 light:text-gray-900"
        onCopy={(event) => event.preventDefault()}
        onCut={(event) => event.preventDefault()}
      >
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-500 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <IconShieldLock size={28} className="shrink-0 text-purple-300" aria-hidden="true" />
            <div className="min-w-0">
              <h1 className="truncate font-heebo text-lg font-semibold">{title}</h1>
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                <span>Sale {saleKey}</span>
                {opened.docScope && (
                  <Badge label={`${capitalize(opened.docScope)} document`}>
                    {capitalize(opened.docScope)} document
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-300" data-testid="irm-opened-by">
            Opened by {stamp}
          </p>
          <Button size="small" type="secondary" label="Close document" onClick={onClose}>
            <IconX size={14} aria-hidden="true" />
            Close document
          </Button>
        </header>

        <div
          role="toolbar"
          aria-label="Document controls"
          className="flex flex-wrap items-center gap-2 border-b border-slate-500 px-4 py-2"
        >
          <ToolButton
            icon={<IconLayoutSidebar size={18} />}
            ariaLabel={sidebar ? 'Hide page thumbnails' : 'Show page thumbnails'}
            tooltipText={sidebar ? 'Hide thumbnails' : 'Show thumbnails'}
            aria-pressed={sidebar}
            className="hidden md:flex"
            onClick={() => setSidebar((value) => !value)}
          />
          <div className="flex items-center gap-1">
            <ToolButton
              icon={<IconChevronUp size={18} />}
              ariaLabel="Previous page"
              tooltipText="Previous page"
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
            />
            <label className="flex items-center gap-1 text-sm text-gray-300">
              <span className="sr-only">Page number</span>
              <TextInput
                inputMode="numeric"
                aria-label="Page number"
                value={pageInput}
                className="w-14 text-center"
                onChange={(event) => setPageInput(event.target.value.replace(/[^0-9]/g, ''))}
                onFocus={() => {
                  editingPage.current = true;
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    editingPage.current = false;
                    goToPage(Number(pageInput) || currentPage);
                    event.currentTarget.blur();
                  }
                }}
                onBlur={() => {
                  const wasEditing = editingPage.current;
                  editingPage.current = false;
                  const typed = Number(pageInput);
                  if (wasEditing && typed > 0 && typed !== currentPage) {
                    goToPage(typed);
                  } else if (wasEditing) {
                    setPageInput(String(currentPage));
                  }
                }}
              />
              <span aria-hidden="true">/ {pageCount || '–'}</span>
              <span className="sr-only">of {pageCount} pages</span>
            </label>
            <ToolButton
              icon={<IconChevronDown size={18} />}
              ariaLabel="Next page"
              tooltipText="Next page"
              disabled={currentPage >= pageCount}
              onClick={() => goToPage(currentPage + 1)}
            />
          </div>

          <div className="flex items-center gap-1">
            <ToolButton
              icon={<IconZoomOut size={18} />}
              ariaLabel="Zoom out"
              tooltipText="Zoom out (−)"
              disabled={effectiveScale <= MIN_ZOOM}
              onClick={() => zoomBy(-ZOOM_STEP)}
            />
            <span
              className="w-14 text-center text-sm tabular-nums text-gray-300"
              title={zoomLabel || undefined}
            >
              {zoomPercent}
            </span>
            <ToolButton
              icon={<IconZoomIn size={18} />}
              ariaLabel="Zoom in"
              tooltipText="Zoom in (+)"
              disabled={effectiveScale >= MAX_ZOOM}
              onClick={() => zoomBy(ZOOM_STEP)}
            />
            <Button
              size="small"
              type={zoom === 'width' ? 'primary' : 'secondary'}
              label="Fit width"
              onClick={() => changeZoom('width')}
            >
              Fit width
            </Button>
            <Button
              size="small"
              type={zoom === 'page' ? 'primary' : 'secondary'}
              label="Fit page"
              onClick={() => changeZoom('page')}
            >
              Fit page
            </Button>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <div ref={searchBox} className="flex items-center gap-1">
              <IconSearch size={16} className="text-gray-400" aria-hidden="true" />
              <TextInput
                type="search"
                placeholder="Find in document"
                aria-label="Find in document"
                disabled={suspended}
                value={query}
                className="w-44"
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    stepMatch(event.shiftKey ? -1 : 1);
                  }
                }}
              />
              <span role="status" className="min-w-[4.5rem] text-xs text-gray-300">
                {searchSummary}
              </span>
              <ToolButton
                icon={<IconChevronUp size={16} />}
                ariaLabel="Previous match"
                tooltipText="Previous match"
                disabled={matches.length === 0}
                onClick={() => stepMatch(-1)}
              />
              <ToolButton
                icon={<IconChevronDown size={16} />}
                ariaLabel="Next match"
                tooltipText="Next match"
                disabled={matches.length === 0}
                onClick={() => stepMatch(1)}
              />
            </div>
            {fullscreenSupported && (
              <ToolButton
                icon={
                  fullscreen ? <IconArrowsMinimize size={18} /> : <IconArrowsMaximize size={18} />
                }
                ariaLabel={fullscreen ? 'Exit full screen' : 'Full screen'}
                tooltipText={fullscreen ? 'Exit full screen' : 'Full screen'}
                onClick={toggleFullscreen}
              />
            )}
          </div>
        </div>

        <div className="flex min-h-0 flex-1" data-testid="irm-document-body">
          {pdfReady && sidebar && (
            <Thumbnails
              pdf={pdfReady.pdf}
              pageCount={pageCount}
              currentPage={currentPage}
              suspended={suspended}
              onSelect={goToPage}
            />
          )}
          <div
            ref={setScroller}
            role="region"
            aria-label="Document pages"
            tabIndex={0}
            className="min-h-0 flex-1 overflow-auto bg-slate-800 py-4 light:bg-gray-300"
          >
            {pdfReady && (
              <div className="flex flex-col" style={{ gap: PAGE_GAP_PX }}>
                {Array.from({ length: pageCount }, (_unused, index) => {
                  const pageNumber = index + 1;
                  return (
                    <PageView
                      key={pageNumber}
                      pdfjs={pdfReady.pdfjs}
                      pdf={pdfReady.pdf}
                      pageNumber={pageNumber}
                      pageCount={pageCount}
                      scale={effectiveScale}
                      placeholder={placeholder}
                      suspended={suspended}
                      root={scroller}
                      highlight={
                        term.length >= MIN_TERM_LENGTH
                          ? {
                              term,
                              activeOrdinal: activePage === pageNumber ? activeOrdinal : undefined,
                              scrollRequest:
                                activePage === pageNumber ? matchScrollRequest : undefined,
                            }
                          : undefined
                      }
                      register={register}
                      onVisibility={onVisibility}
                    />
                  );
                })}
              </div>
            )}
            {loaded.status === 'loading' && (
              <div role="status" className="flex flex-col items-center gap-3 p-12 text-gray-300">
                <SpinnerComponent loading positionRelative />
                Preparing the document…
              </div>
            )}
            {loaded.status === 'error' && (
              <div role="alert" className="flex flex-col items-center gap-3 p-12 text-center">
                <p className="font-heebo text-lg font-semibold">This document can’t be displayed</p>
                <p className="max-w-md text-sm text-gray-300">
                  The file opened but couldn’t be drawn. Close it and open it again from the file
                  you downloaded.
                </p>
                <Button label="Close document" type="secondary" onClick={onClose}>
                  Close document
                </Button>
              </div>
            )}
          </div>
        </div>

        <StampOverlay text={stamp} stampCount={stampCount} />
        {notice && (
          <p
            role="status"
            className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-md border border-slate-500 bg-gray-900 px-4 py-2 text-sm text-white shadow-lg"
          >
            {notice}
          </p>
        )}
        <p className="sr-only" aria-live="polite">
          {pageCount > 0 ? `Page ${currentPage} of ${pageCount}` : ''}
        </p>
      </div>
      {suspended && (
        <div
          role="status"
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 bg-background-canvas text-gray-200 light:bg-gray-100 light:text-gray-700"
        >
          <SpinnerComponent loading positionRelative />
          <p>Checking your access…</p>
        </div>
      )}
    </div>
  );
};
